/**
 * Anant Smart WebShop — B2C micro-shop suite.
 * Blueprint v2: 3-day strict trial → Monthly 600 AT / Yearly 6000 AT.
 * 20 AT per AI product description. Repeat-buy blocked while active.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Sparkles, Store, Package, Share2, Trash2, Save, Mic, Square, Upload, FileText, Music, Camera, Image as ImageIcon, AlertTriangle, Video, Youtube, Bell, FileSpreadsheet } from 'lucide-react';
import api from '../../utils/api';
import AIWalletGuard from '../../components/AIWalletGuard';
import ClientContactActions from '../../components/ClientContactActions';

const TABS = [
  { key: 'shop',     label: 'My Shop' },
  { key: 'products', label: 'Products' },
  { key: 'orders',   label: 'Orders' },
  { key: 'share',    label: 'Share Cards' },
  { key: 'analytics',label: 'Analytics' },
  { key: 'plan',     label: 'Plan' },
];

export default function AnantWebShop() {
  const [cfg, setCfg] = useState(null);
  const [tab, setTab] = useState('shop');
  const load = useCallback(() => api.get('/webshop/config').then((r) => setCfg(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  if (!cfg) return <div className="p-6 text-center text-emerald-300">Loading...</div>;
  const active = !!cfg.active;
  const trial = cfg.user?.plan === 'trial';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-emerald-950/40 to-slate-950 text-white p-3 sm:p-6" data-testid="webshop-root">
      <div className="max-w-5xl mx-auto space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-2xl sm:text-3xl font-black tracking-tight bg-gradient-to-r from-emerald-300 via-teal-300 to-amber-300 bg-clip-text text-transparent">🏪 Anant Smart WebShop</div>
            <div className="text-xs opacity-70">B2C micro-shops · AI descriptions · Product share cards · 20 AT per AI</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <a href="/wallets" data-testid="webshop-at-wallet"
               className="px-3 py-1 rounded-full text-xs font-black bg-gradient-to-r from-amber-500/30 to-orange-500/30 border-2 border-amber-400/50 text-amber-100 hover:bg-amber-500/40 transition">
              💰 {Math.round(cfg.at_balance || 0)} AT
            </a>
            <PlanStatusBadge cfg={cfg} />
          </div>
        </div>

        <div className="flex gap-2 overflow-x-auto pb-1">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`webshop-tab-${t.key}`}
              className={`relative px-3 py-1.5 rounded-lg text-xs font-black border-2 whitespace-nowrap ${tab === t.key ? 'bg-emerald-500 border-emerald-300 text-slate-900' : 'bg-slate-900 border-slate-700'}`}>
              {t.label}
              {t.key === 'orders' && cfg.unread_orders > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] flex items-center justify-center font-black" data-testid="webshop-orders-badge">
                  {cfg.unread_orders > 99 ? '99+' : cfg.unread_orders}
                </span>
              )}
            </button>
          ))}
        </div>

        {trial && active && (
          <div className="rounded-xl bg-amber-500/15 border border-amber-400/40 p-2 text-center text-[11px] text-amber-200" data-testid="webshop-trial-banner">
            🎁 <b>3-day Trial active</b> · <CountdownTimer expiry={cfg.user?.plan_expiry} compact onExpire={load} /> · Plan tab me jaake convert karo
          </div>
        )}

        {tab === 'shop'     && <ShopTab cfg={cfg} active={active} onReload={load} />}
        {tab === 'products' && <ProductsTab cfg={cfg} active={active} onReload={load} />}
        {tab === 'orders'   && <OrdersTab cfg={cfg} active={active} onReload={load} />}
        {tab === 'share'    && <ShareTab cfg={cfg} active={active} />}
        {tab === 'analytics'&& <AnalyticsTab active={active} />}
        {tab === 'plan'     && <PlanTab cfg={cfg} onReload={load} />}
      </div>
    </div>
  );
}

// ============ SHOP TAB ============
function ShopTab({ cfg, active, onReload }) {
  const s = cfg.user?.shop || {};
  const [form, setForm] = useState({
    name: s.name || '', tagline: s.tagline || '', logo_url: s.logo_url || '',
    banner_url: s.banner_url || '', city: s.city || '', whatsapp: s.whatsapp || '',
    mode: s.mode || 'b2c',
    fulfillment: s.fulfillment || 'both',
    delivery_cities: s.delivery_cities || '',
    pickup_address: s.pickup_address || '',
  });
  const [busy, setBusy] = useState(false);
  if (!active) return <SubscribeGate title="WebShop locked" body="3-day trial khatam ho gaya ya plan expire. Plan tab me jaake activate karo." />;
  const save = async () => {
    if (!form.name.trim()) { toast.error('Shop name required'); return; }
    setBusy(true);
    try { await api.post('/webshop/shop', form); toast.success('✅ Shop saved'); onReload && onReload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const publicUrl = s.slug ? `${window.location.origin}/webshop/${s.slug}` : null;
  return (
    <div className="space-y-3">
      {publicUrl && (
        <div className="rounded-xl bg-emerald-500/10 border border-emerald-400/40 p-3 flex items-center gap-2" data-testid="webshop-public-url">
          <div className="flex-1 text-xs">
            <div className="text-[10px] uppercase opacity-70">Public Shop URL</div>
            <div className="font-mono text-emerald-200 truncate">{publicUrl}</div>
          </div>
          <button onClick={() => { navigator.clipboard.writeText(publicUrl); toast.success('Copied'); }}
                  className="px-2 py-1 rounded bg-emerald-500 text-slate-900 text-[10px] font-black">📋 Copy</button>
          <a href={publicUrl} target="_blank" rel="noreferrer" className="px-2 py-1 rounded bg-slate-700 text-white text-[10px] font-black">🔗 Open</a>
        </div>
      )}
      <div className="p-3 rounded-2xl bg-slate-900 border border-slate-700 space-y-2" data-testid="webshop-shop-form">
        <div className="text-xs font-black text-emerald-300 flex items-center gap-1"><Store size={12} /> Shop Details</div>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Shop Name *" data-testid="webshop-shop-name" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="Tagline (e.g. Fresh sarees direct from mill)" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
          <input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="WhatsApp number" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        </div>
        <input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="Logo URL" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <input value={form.banner_url} onChange={(e) => setForm({ ...form, banner_url: e.target.value })} placeholder="Banner URL" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm">
          <option value="b2c">B2C · Retail</option>
          <option value="b2b">B2B · Wholesale (tiered pricing)</option>
          <option value="both">Both</option>
        </select>

        {/* Fulfillment / City-wise Delivery */}
        <div className="rounded-lg bg-slate-800/50 border border-slate-700 p-2 space-y-2" data-testid="webshop-fulfillment-block">
          <div className="text-[10px] font-black text-cyan-300 uppercase">🚚 Fulfillment mode (default for all products)</div>
          <select value={form.fulfillment} onChange={(e) => setForm({ ...form, fulfillment: e.target.value })}
                  data-testid="webshop-shop-fulfillment"
                  className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm">
            <option value="both">🚚 Home Delivery + 🏪 Offline Pickup (Both)</option>
            <option value="online_delivery">🚚 Online / Home Delivery only</option>
            <option value="offline_pickup">🏪 Offline / Pickup only (no delivery)</option>
          </select>
          {(form.fulfillment === 'online_delivery' || form.fulfillment === 'both') && (
            <input value={form.delivery_cities} onChange={(e) => setForm({ ...form, delivery_cities: e.target.value })}
                   placeholder="Delivery cities (comma-separated, e.g. Mumbai, Pune, Nagpur)"
                   data-testid="webshop-delivery-cities"
                   className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-xs" />
          )}
          {(form.fulfillment === 'offline_pickup' || form.fulfillment === 'both') && (
            <input value={form.pickup_address} onChange={(e) => setForm({ ...form, pickup_address: e.target.value })}
                   placeholder="Pickup address (shop location for offline customers)"
                   data-testid="webshop-pickup-address"
                   className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-xs" />
          )}
        </div>

        <button onClick={save} disabled={busy} data-testid="webshop-shop-save"
                className="w-full py-2.5 rounded-xl bg-emerald-500 text-slate-900 text-sm font-black disabled:opacity-50">
          <Save size={12} className="inline mr-1" />{busy ? 'Saving...' : 'Save Shop'}
        </button>
      </div>
    </div>
  );
}

// ============ PRODUCTS TAB ============
function ProductsTab({ cfg, active, onReload }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name: '', description: '', price: '', category: '', image_url: '', stock: '', discount_badge: '', mode: 'b2c', moq: '', audio_url: '', audio_name: '', attachment_url: '', attachment_name: '', attachment_type: '', fulfillment: '', video_url: '', video_kind: '' });
  const [tiers, setTiers] = useState([]); // {min_qty, price}
  const [aiBusy, setAiBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null); // 'audio' | 'file' | 'record' | 'image' | 'video'
  const [recording, setRecording] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const audioInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const csvInputRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const atBalance = Number(cfg?.at_balance || 0);
  const LOW_AT_THRESHOLD = 200;
  const lowAt = atBalance < LOW_AT_THRESHOLD;
  const productLimit = Number(cfg?.product_limit || 50);
  const productsCount = rows.length;
  const limitReached = productsCount >= productLimit;
  const load = () => api.get('/webshop/products').then((r) => setRows(r.data.products || [])).catch(() => {});
  useEffect(() => { if (active) load(); }, [active]);
  if (!active) return <SubscribeGate title="Products locked" body="Plan tab me jaake trial/subscription activate karo." />;

  const aiDesc = async () => {
    if (!form.name.trim()) { toast.error('Product name first'); return; }
    if (atBalance < 20) { toast.error(`Sirf ${atBalance} AT — 20 chahiye. Top up karo!`); return; }
    const msg = lowAt
      ? `⚠️ Sirf ${atBalance} AT bacha hai. AI Description pe 20 AT deduct hoga. Continue?`
      : 'AI Description par 20 AT deduct hoga. Continue?';
    if (!window.confirm(msg)) return;
    setAiBusy(true);
    try {
      const r = await api.post('/webshop/ai/description', { product_name: form.name, hints: form.description || '', language: 'hi', tone: 'persuasive' });
      setForm((f) => ({ ...f, description: r.data.description || f.description }));
      toast.success('✨ 20 AT charged · AI description ready');
    } catch (e) { toast.error(e?.response?.data?.detail || 'AI failed'); }
    finally { setAiBusy(false); }
  };

  const uploadAudioFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading('audio');
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await api.post('/webshop/upload/audio', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((prev) => ({ ...prev, audio_url: r.data.url, audio_name: r.data.name }));
      toast.success('🎙 Audio uploaded');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(null); if (audioInputRef.current) audioInputRef.current.value = ''; }
  };

  const uploadImage = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading('image');
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await api.post('/webshop/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((prev) => ({ ...prev, image_url: r.data.url }));
      toast.success('🖼 Image uploaded');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    finally {
      setUploading(null);
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (galleryInputRef.current) galleryInputRef.current.value = '';
    }
  };

  const uploadVideo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading('video');
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await api.post('/webshop/upload/video', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((prev) => ({ ...prev, video_url: r.data.url, video_kind: 'upload' }));
      toast.success('🎬 Video uploaded');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed — YouTube link recommended'); }
    finally { setUploading(null); if (videoInputRef.current) videoInputRef.current.value = ''; }
  };

  const parseCSV = (text) => {
    // Minimal CSV parser (handles quoted fields with commas, doubled quotes)
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
    if (lines.length === 0) return [];
    const parseRow = (line) => {
      const out = []; let cur = ''; let inQ = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (inQ) {
          if (ch === '"' && line[i + 1] === '"') { cur += '"'; i++; }
          else if (ch === '"') { inQ = false; }
          else { cur += ch; }
        } else if (ch === '"') { inQ = true; }
        else if (ch === ',') { out.push(cur); cur = ''; }
        else { cur += ch; }
      }
      out.push(cur);
      return out.map((x) => x.trim());
    };
    const headers = parseRow(lines[0]).map((h) => h.toLowerCase().replace(/\s+/g, '_'));
    return lines.slice(1).map((l) => {
      const cells = parseRow(l);
      const obj = {}; headers.forEach((h, i) => { obj[h] = cells[i] ?? ''; });
      return obj;
    });
  };

  const importCSV = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBulkBusy(true);
    try {
      const text = await f.text();
      const rowsRaw = parseCSV(text);
      if (rowsRaw.length === 0) { toast.error('CSV empty'); return; }
      const products = rowsRaw.map((r) => ({
        name: (r.name || r.product || '').slice(0, 120),
        description: r.description || r.desc || '',
        price: Number(r.price || 0),
        category: r.category || '',
        image_url: r.image_url || r.image || '',
        stock: r.stock ? Number(r.stock) : null,
        moq: r.moq ? Number(r.moq) : null,
        mode: (r.mode || 'b2c').toLowerCase(),
        discount_badge: r.discount_badge || r.discount || '',
        video_url: r.video_url || r.video || r.youtube || '',
        video_kind: (r.video_url || r.video || '').includes('youtu') ? 'youtube' : (r.video_url || r.video ? 'direct' : ''),
        fulfillment: r.fulfillment || '',
        active: true,
      })).filter((p) => p.name && p.price >= 0);
      if (products.length === 0) { toast.error('Kuch valid rows nahi (needs name + price)'); return; }
      if (!window.confirm(`${products.length} products import karne hain?`)) return;
      const resp = await api.post('/webshop/products/bulk-import', { products });
      toast.success(`✅ ${resp.data.inserted} imported${resp.data.skipped ? ` · ${resp.data.skipped} skipped (limit)` : ''}`);
      setBulkOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'CSV parse/import failed'); }
    finally { setBulkBusy(false); if (csvInputRef.current) csvInputRef.current.value = ''; }
  };

  const downloadCSVTemplate = () => {
    const csv = 'name,description,price,category,image_url,stock,moq,mode,discount_badge,video_url,fulfillment\n"Sample Saree","Fresh cotton saree",1499,"Sarees","",50,,b2c,"20% OFF","https://youtu.be/xyz","both"\n';
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    a.download = 'webshop-products-template.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const uploadAttachment = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading('file');
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await api.post('/webshop/upload/file', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((prev) => ({ ...prev, attachment_url: r.data.url, attachment_name: r.data.name, attachment_type: r.data.type }));
      toast.success('📎 File uploaded');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(null); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const startRecord = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeCandidates = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/webm', 'audio/mp4'];
      const mime = mimeCandidates.find((m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)) || '';
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data); };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime || 'audio/webm' });
        const ext = (mime.includes('ogg') || mime.includes('opus')) ? 'opus' : (mime.includes('mp4') ? 'm4a' : 'webm');
        const file = new File([blob], `voice-${Date.now()}.${ext}`, { type: blob.type });
        setUploading('record');
        try {
          const fd = new FormData(); fd.append('file', file);
          const r = await api.post('/webshop/upload/audio', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          setForm((prev) => ({ ...prev, audio_url: r.data.url, audio_name: r.data.name }));
          toast.success('🎙 Voice note saved');
        } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
        finally { setUploading(null); }
      };
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
      toast.success('🔴 Recording… tap stop when done');
    } catch (e) { toast.error('Mic access denied'); }
  };

  const stopRecord = () => {
    try { recorderRef.current?.stop(); } catch {}
    setRecording(false);
  };

  const save = async () => {
    if (!form.name.trim() || !form.price) { toast.error('Name + price required'); return; }
    if (limitReached) { toast.error(`Product limit reached (${productsCount}/${productLimit})`); return; }
    setBusy(true);
    try {
      await api.post('/webshop/product', {
        ...form,
        price: Number(form.price),
        stock: form.stock ? Number(form.stock) : null,
        moq: form.moq ? Number(form.moq) : null,
        tiers: tiers.filter((t) => t.min_qty && t.price).map((t) => ({ min_qty: Number(t.min_qty), price: Number(t.price) })),
        active: true,
      });
      toast.success('✅ Product added');
      setForm({ name: '', description: '', price: '', category: '', image_url: '', stock: '', discount_badge: '', mode: 'b2c', moq: '', audio_url: '', audio_name: '', attachment_url: '', attachment_name: '', attachment_type: '', fulfillment: '', video_url: '', video_kind: '' });
      setTiers([]);
      load();
      onReload && onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await api.delete(`/webshop/product/${id}`); load(); toast.success('Deleted');
  };

  return (
    <div className="space-y-3">
      <AIWalletGuard atBalance={atBalance} threshold={LOW_AT_THRESHOLD} tool="WebShop AI" testId="webshop-wallet-guard-banner" />

      {/* Product limit + bulk import strip */}
      <div className="rounded-xl bg-slate-900 border border-slate-700 p-2 flex flex-wrap items-center gap-2" data-testid="webshop-limit-strip">
        <div className="flex-1 min-w-[120px] text-[11px]">
          <span className="opacity-70">Products:</span>{' '}
          <b className={limitReached ? 'text-rose-300' : 'text-emerald-300'}>{productsCount}/{productLimit}</b>
          {limitReached && <span className="ml-1 text-[9px] text-rose-300">🚫 Limit reached — admin se badhwao</span>}
        </div>
        <button onClick={() => setBulkOpen((v) => !v)} data-testid="webshop-bulk-toggle"
                className="px-2 py-1 rounded bg-cyan-500 text-slate-900 text-[10px] font-black flex items-center gap-1">
          <FileSpreadsheet size={11} /> {bulkOpen ? 'Close' : 'Bulk Import CSV'}
        </button>
      </div>

      {bulkOpen && (
        <div className="rounded-xl bg-cyan-500/10 border-2 border-cyan-400/40 p-3 space-y-2" data-testid="webshop-bulk-block">
          <div className="text-[10px] font-black text-cyan-300 uppercase">📋 Bulk Import (CSV / Excel-exported CSV)</div>
          <div className="text-[10px] opacity-80">Columns: <code>name, description, price, category, image_url, stock, moq, mode, discount_badge, video_url, fulfillment</code>. Ek CSV me max 200 products. Product limit apply hoga.</div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={downloadCSVTemplate} data-testid="webshop-bulk-template"
                    className="px-2 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-xs font-black">
              ⬇️ Download CSV template
            </button>
            <button onClick={() => csvInputRef.current?.click()} disabled={bulkBusy}
                    data-testid="webshop-bulk-upload"
                    className="px-2 py-2 rounded-lg bg-cyan-500 text-slate-900 text-xs font-black disabled:opacity-50">
              {bulkBusy ? 'Importing…' : '📤 Upload CSV'}
            </button>
          </div>
          <input ref={csvInputRef} type="file" accept=".csv,text/csv" onChange={importCSV} className="hidden" data-testid="webshop-bulk-file-input" />
        </div>
      )}

      <div className="p-3 rounded-2xl bg-slate-900 border border-slate-700 space-y-2" data-testid="webshop-product-form">
        <div className="text-xs font-black text-emerald-300 flex items-center gap-1"><Package size={12} /> Add Product</div>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Product name *" data-testid="webshop-product-name" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <div className="flex gap-1">
          <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description (or AI generate)" className="flex-1 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm resize-y" />
          <button onClick={aiDesc} disabled={aiBusy} data-testid="webshop-ai-desc"
                  className={`px-2 rounded-lg text-white text-[10px] font-black disabled:opacity-50 flex items-center gap-1 ${lowAt ? 'bg-amber-500 ring-2 ring-amber-300 animate-pulse' : 'bg-purple-500'}`}
                  title={lowAt ? `Low balance: ${atBalance} AT — will attempt 20 AT charge` : 'AI Description · 20 AT'}>
            <Sparkles size={12} />{aiBusy ? '⏳' : (lowAt ? `⚠️ 20 AT` : '20 AT')}
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <input value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="Price *" type="number" data-testid="webshop-product-price" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
          <input value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} placeholder="Stock" type="number" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
          <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Category" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        </div>
        <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="Image URL (or use Camera/Gallery below)" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        {/* Image upload — Camera + Gallery (mobile-first) */}
        <div className="rounded-lg bg-slate-800/50 border border-slate-700 p-2 space-y-2" data-testid="webshop-image-block">
          <div className="text-[10px] font-black text-emerald-300 uppercase flex items-center gap-1"><ImageIcon size={11} /> Product image (max 8MB)</div>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => cameraInputRef.current?.click()} disabled={!!uploading}
                    data-testid="webshop-image-camera-btn"
                    className="flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black disabled:opacity-50">
              <Camera size={13} /> {uploading === 'image' ? 'Uploading…' : 'Camera'}
            </button>
            <button type="button" onClick={() => galleryInputRef.current?.click()} disabled={!!uploading}
                    data-testid="webshop-image-gallery-btn"
                    className="flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-xs font-black disabled:opacity-50">
              <ImageIcon size={13} /> {uploading === 'image' ? 'Uploading…' : 'Gallery'}
            </button>
          </div>
          <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" onChange={uploadImage} className="hidden" data-testid="webshop-image-camera-input" />
          <input ref={galleryInputRef} type="file" accept="image/*,.jpg,.jpeg,.png,.webp,.gif,.heic" onChange={uploadImage} className="hidden" data-testid="webshop-image-gallery-input" />
          {form.image_url && (
            <div className="rounded bg-slate-900/70 p-1.5 flex items-center gap-2" data-testid="webshop-image-preview">
              <img src={form.image_url.startsWith('http') ? form.image_url : `${process.env.REACT_APP_BACKEND_URL || ''}${form.image_url}`} alt="preview" className="w-14 h-14 object-cover rounded" />
              <div className="text-[10px] text-emerald-300 truncate flex-1">🖼 Image attached</div>
              <button type="button" onClick={() => setForm({ ...form, image_url: '' })} className="text-[10px] text-rose-300 font-black" data-testid="webshop-image-clear">✕ Remove</button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })} data-testid="webshop-product-mode" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm">
            <option value="b2c">B2C · Retail</option>
            <option value="b2b">B2B · Wholesale</option>
            <option value="both">Both</option>
          </select>
          <input value={form.moq} onChange={(e) => setForm({ ...form, moq: e.target.value })} placeholder="MOQ (min order qty)" type="number" data-testid="webshop-product-moq" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        </div>

        {/* Tiered pricing for B2B */}
        {(form.mode === 'b2b' || form.mode === 'both') && (
          <div className="rounded-lg bg-slate-800/60 border border-slate-700 p-2 space-y-1" data-testid="webshop-tiers-block">
            <div className="text-[10px] font-black text-emerald-300 uppercase flex items-center justify-between">
              <span>💼 Bulk tiered pricing</span>
              <button type="button" onClick={() => setTiers([...tiers, { min_qty: '', price: '' }])} data-testid="webshop-tier-add"
                      className="text-[10px] px-2 py-0.5 rounded bg-emerald-500 text-slate-900 font-black">+ Add tier</button>
            </div>
            {tiers.map((t, i) => (
              <div key={i} className="grid grid-cols-12 gap-1 items-center" data-testid={`webshop-tier-${i}`}>
                <input value={t.min_qty} onChange={(e) => setTiers(tiers.map((x, j) => j === i ? { ...x, min_qty: e.target.value } : x))} placeholder="Min qty" type="number" className="col-span-5 px-2 py-1 rounded bg-slate-800 border border-slate-700 text-[10px]" />
                <input value={t.price} onChange={(e) => setTiers(tiers.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} placeholder="Unit price" type="number" className="col-span-5 px-2 py-1 rounded bg-slate-800 border border-slate-700 text-[10px]" />
                <button onClick={() => setTiers(tiers.filter((_, j) => j !== i))} className="col-span-2 text-rose-300 text-[10px]">✕</button>
              </div>
            ))}
          </div>
        )}

        <input value={form.audio_url} onChange={(e) => setForm({ ...form, audio_url: e.target.value, audio_name: e.target.value ? (form.audio_name || 'external') : '' })} placeholder="🎙 Audio/jingle URL (voice note or MP3) — or use buttons below" data-testid="webshop-product-audio" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        {/* Audio: Upload · Record · Attachment */}
        <div className="rounded-lg bg-slate-800/50 border border-slate-700 p-2 space-y-2" data-testid="webshop-audio-block">
          <div className="text-[10px] font-black text-amber-300 uppercase flex items-center gap-1"><Music size={11} /> Audio (MP3/OPUS/WEBM · max 10MB)</div>
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => audioInputRef.current?.click()} disabled={!!uploading || recording}
                    data-testid="webshop-audio-upload-btn"
                    className="flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-xs font-black disabled:opacity-50">
              <Upload size={13} /> {uploading === 'audio' ? 'Uploading…' : 'Upload MP3'}
            </button>
            {!recording ? (
              <button type="button" onClick={startRecord} disabled={!!uploading}
                      data-testid="webshop-audio-record-btn"
                      className="flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-xs font-black disabled:opacity-50">
                <Mic size={13} /> {uploading === 'record' ? 'Saving…' : 'Record Voice'}
              </button>
            ) : (
              <button type="button" onClick={stopRecord} data-testid="webshop-audio-stop-btn"
                      className="flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-red-700 animate-pulse text-white text-xs font-black">
                <Square size={13} /> Stop
              </button>
            )}
          </div>
          <input ref={audioInputRef} type="file" accept="audio/*,.mp3,.opus,.ogg,.wav,.m4a,.webm,.aac" onChange={uploadAudioFile} className="hidden" data-testid="webshop-audio-file-input" />
          {form.audio_url && (
            <div className="rounded bg-slate-900/70 p-2 space-y-1" data-testid="webshop-audio-preview">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] text-emerald-300 font-black truncate flex-1">🎙 {form.audio_name || 'audio.mp3'}</div>
                <button type="button" onClick={() => setForm({ ...form, audio_url: '', audio_name: '' })} className="text-[10px] text-rose-300" data-testid="webshop-audio-clear">✕ Remove</button>
              </div>
              <audio controls src={form.audio_url.startsWith('http') ? form.audio_url : `${process.env.REACT_APP_BACKEND_URL || ''}${form.audio_url}`} className="w-full h-8" />
            </div>
          )}
        </div>

        {/* Video (URL / YouTube / Gallery — user's own device or hosted) */}
        <div className="rounded-lg bg-slate-800/50 border border-slate-700 p-2 space-y-2" data-testid="webshop-video-block">
          <div className="text-[10px] font-black text-rose-300 uppercase flex items-center gap-1"><Video size={11} /> Product Video (YouTube 0-cost · upload 20MB max)</div>
          <input value={form.video_url} onChange={(e) => {
            const v = e.target.value.trim();
            setForm({ ...form, video_url: v, video_kind: v.includes('youtu') ? 'youtube' : (v ? 'direct' : '') });
          }} placeholder="🔗 YouTube link ya direct .mp4 URL" data-testid="webshop-video-url"
             className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-xs" />
          <div className="grid grid-cols-2 gap-2">
            <a href="https://youtube.com/upload" target="_blank" rel="noreferrer" data-testid="webshop-yt-open"
               className="flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-black">
              <Youtube size={13} /> Upload to YouTube
            </a>
            <button type="button" onClick={() => videoInputRef.current?.click()} disabled={!!uploading}
                    data-testid="webshop-video-upload-btn"
                    className="flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-xs font-black disabled:opacity-50">
              <Upload size={13} /> {uploading === 'video' ? 'Uploading…' : 'Gallery Upload'}
            </button>
          </div>
          <input ref={videoInputRef} type="file" accept="video/*,.mp4,.mov,.webm,.m4v" onChange={uploadVideo} className="hidden" data-testid="webshop-video-file-input" />
          {form.video_url && (
            <div className="rounded bg-slate-900/70 p-1.5 flex items-center gap-2" data-testid="webshop-video-preview">
              <Video size={13} className="text-rose-300" />
              <div className="flex-1 text-[10px] text-rose-200 font-black truncate">
                {form.video_kind === 'youtube' ? '📺 YouTube embed' : form.video_kind === 'upload' ? '🎬 Uploaded video' : '🔗 Direct video URL'}
              </div>
              <button type="button" onClick={() => setForm({ ...form, video_url: '', video_kind: '' })} className="text-[10px] text-rose-300 font-black" data-testid="webshop-video-clear">✕ Remove</button>
            </div>
          )}
        </div>

        {/* Text/PDF attachment */}
        <div className="rounded-lg bg-slate-800/50 border border-slate-700 p-2 space-y-2" data-testid="webshop-attach-block">
          <div className="text-[10px] font-black text-cyan-300 uppercase flex items-center gap-1"><FileText size={11} /> Attachment (PDF/TXT/DOC · max 15MB)</div>
          <button type="button" onClick={() => fileInputRef.current?.click()} disabled={!!uploading}
                  data-testid="webshop-attach-upload-btn"
                  className="w-full flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-xs font-black disabled:opacity-50">
            <Upload size={13} /> {uploading === 'file' ? 'Uploading…' : 'Upload PDF / TXT / DOC'}
          </button>
          <input ref={fileInputRef} type="file" accept=".pdf,.txt,.md,.csv,.doc,.docx,.jpg,.jpeg,.png,.webp,application/pdf,text/plain" onChange={uploadAttachment} className="hidden" data-testid="webshop-attach-file-input" />
          {form.attachment_url && (
            <div className="rounded bg-slate-900/70 p-2 flex items-center gap-2" data-testid="webshop-attach-preview">
              <FileText size={14} className="text-cyan-300" />
              <a href={form.attachment_url.startsWith('http') ? form.attachment_url : `${process.env.REACT_APP_BACKEND_URL || ''}${form.attachment_url}`} target="_blank" rel="noreferrer" className="text-[10px] text-cyan-200 font-black truncate flex-1 underline">{form.attachment_name || 'file'} · {form.attachment_type}</a>
              <button type="button" onClick={() => setForm({ ...form, attachment_url: '', attachment_name: '', attachment_type: '' })} className="text-[10px] text-rose-300" data-testid="webshop-attach-clear">✕</button>
            </div>
          )}
        </div>

        <input value={form.discount_badge} onChange={(e) => setForm({ ...form, discount_badge: e.target.value })} placeholder="Discount badge (e.g. 20% OFF)" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        {/* Per-product fulfillment override */}
        <select value={form.fulfillment} onChange={(e) => setForm({ ...form, fulfillment: e.target.value })}
                data-testid="webshop-product-fulfillment"
                className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-xs">
          <option value="">🚚 Fulfillment · Use shop default</option>
          <option value="online_delivery">🚚 Online / Home Delivery only</option>
          <option value="offline_pickup">🏪 Offline / Pickup only</option>
          <option value="both">🚚+🏪 Both delivery & pickup</option>
        </select>
        <button onClick={save} disabled={busy || limitReached} data-testid="webshop-product-save"
                className={`w-full py-2 rounded-xl text-slate-900 text-sm font-black disabled:opacity-50 ${limitReached ? 'bg-rose-500' : 'bg-emerald-500'}`}>
          {limitReached ? `🚫 Limit ${productsCount}/${productLimit}` : (busy ? 'Adding...' : '➕ Add Product')}
        </button>
      </div>
      <div className="space-y-2" data-testid="webshop-products-list">
        {rows.length === 0 && <div className="text-xs opacity-60 text-center py-6">Koi product nahi.</div>}
        {rows.map((p) => (
          <div key={p.id} className="p-3 rounded-xl bg-slate-900 border border-slate-800 flex items-center gap-3" data-testid={`webshop-product-${p.id}`}>
            {p.image_url ? <img src={p.image_url} alt="" className="w-14 h-14 object-cover rounded" /> :
              <div className="w-14 h-14 rounded bg-slate-800 flex items-center justify-center text-2xl">📦</div>}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-black truncate">{p.name} {p.audio_url && <span className="ml-1 text-[10px] text-amber-300">🎙</span>}{p.attachment_url && <span className="ml-1 text-[10px] text-cyan-300">📎</span>}{p.video_url && <span className="ml-1 text-[10px] text-rose-300">🎬</span>}{p.fulfillment === 'online_delivery' && <span className="ml-1 text-[9px] text-sky-300">🚚</span>}{p.fulfillment === 'offline_pickup' && <span className="ml-1 text-[9px] text-orange-300">🏪</span>}{p.fulfillment === 'both' && <span className="ml-1 text-[9px] text-emerald-300">🚚🏪</span>}</div>
              <div className="text-[10px] opacity-70 line-clamp-2">{p.description}</div>
              <div className="text-[11px] text-emerald-300 font-black">₹{p.price}{p.moq ? ` · MOQ ${p.moq}` : ''}{p.tiers?.length ? ` · ${p.tiers.length} tiers` : ''} {p.discount_badge && <span className="ml-1 px-1.5 py-0.5 rounded bg-red-500/30 text-red-200">{p.discount_badge}</span>}</div>
            </div>
            <div className="flex flex-col gap-1">
              <button onClick={async () => {
                        if (!window.confirm('⭐ Boost on Marketplace? 500 AT charge hoga (7-day sponsored slot + marketplace listing).')) return;
                        try {
                          const r = await api.post(`/webshop/product/${p.id}/boost`);
                          toast.success(`✅ Boosted · ${r.data.at_charged} AT charged`);
                          window.open(`/marketplace/store/${r.data.shop_slug}#listing-${r.data.listing?.id}`, '_blank');
                        } catch (e) { toast.error(e?.response?.data?.detail || 'Boost failed'); }
                      }}
                      data-testid={`webshop-boost-${p.id}`}
                      title="Boost this product on Marketplace · 500 AT / 7 days"
                      className="px-2 py-1 rounded bg-gradient-to-r from-yellow-400 to-amber-500 text-slate-900 text-[9px] font-black whitespace-nowrap">
                ⭐ Boost
              </button>
              <button onClick={() => del(p.id)} className="px-2 py-1 rounded bg-red-500/30 text-red-300 text-[10px] font-black"><Trash2 size={12} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ SHARE CARDS TAB (canvas render + native share per product) ============
function ShareTab({ cfg, active }) {
  const [rows, setRows] = useState([]);
  const [pick, setPick] = useState(null);
  const [filter, setFilter] = useState('none');
  const [frame, setFrame] = useState('none');
  const [whiteLabel, setWhiteLabel] = useState(false);
  const [msgTpl, setMsgTpl] = useState('smart');
  const [customMsg, setCustomMsg] = useState('');
  const [format, setFormat] = useState('square'); // square / story / landscape
  const [qrMode, setQrMode] = useState(false);
  const canvasRef = useRef(null);
  useEffect(() => { if (active) api.get('/webshop/products').then((r) => setRows(r.data.products || [])).catch(() => {}); }, [active]);

  const shopSlug = cfg.user?.shop?.slug || '';
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const publicShopUrl = shopSlug ? `${window.location.origin}/webshop/${shopSlug}` : '';
  // OG-preview share URL — WhatsApp/FB/IG scrape THIS URL and read product/shop image tags
  const shareUrl = pick && shopSlug ? `${API_BASE}/api/share/webshop/${shopSlug}/${pick.id}`
                   : shopSlug ? `${API_BASE}/api/share/webshop/${shopSlug}`
                   : publicShopUrl;
  const productUrl = shareUrl; // used inside message body so preview image loads

  // Auto-preset message
  const buildMessage = () => {
    if (!pick) return '';
    const shop = cfg.user?.shop || {};
    if (msgTpl === 'custom' && customMsg) return customMsg;
    const priceLine = whiteLabel ? '' : `\n💰 ₹${pick.price}${pick.discount_badge ? ' · ' + pick.discount_badge : ''}`;
    const shopLine = `\n\n🏪 ${shop.name || ''}${shop.whatsapp ? '  📞 ' + shop.whatsapp : ''}\n🔗 ${productUrl}`;
    const map = {
      smart:   `✨ *${pick.name}*${priceLine}\n${pick.description || ''}${shopLine}`,
      festive: `🎉 *Festive Special · ${pick.name}*${priceLine}\n🌟 Aaj hi order karein — ${pick.description || ''}${shopLine}`,
      sale:    `🔥 *LIMITED SALE* · ${pick.name}${priceLine}\n⏰ Stock khatam hone se pehle grab karein!${shopLine}`,
      luxury:  `👑 *${pick.name}*${priceLine}\n${pick.description || ''}\nCrafted for those who choose the finest.${shopLine}`,
      urgent:  `⚡ *ABHI HI!* ${pick.name}${priceLine}\n🚚 Fastest delivery available. Book karein!${shopLine}`,
    };
    return map[msgTpl] || map.smart;
  };

  useEffect(() => {
    const c = canvasRef.current; if (!c || !pick) return;
    const dims = format === 'story' ? [1080, 1920] : format === 'landscape' ? [1200, 630] : [1080, 1080];
    c.width = dims[0]; c.height = dims[1];
    const ctx = c.getContext('2d');
    // Brand gradient BG
    const g = ctx.createLinearGradient(0, 0, c.width, c.height);
    g.addColorStop(0, '#059669'); g.addColorStop(0.5, '#0f766e'); g.addColorStop(1, '#facc15');
    ctx.fillStyle = g; ctx.fillRect(0, 0, c.width, c.height);

    const applyFilter = () => {
      if (filter === 'none') return;
      const imgd = ctx.getImageData(0, 0, c.width, c.height);
      const d = imgd.data;
      for (let i = 0; i < d.length; i += 4) {
        const r = d[i], gr = d[i + 1], b = d[i + 2];
        if (filter === 'grayscale') { const v = (r + gr + b) / 3; d[i] = d[i + 1] = d[i + 2] = v; }
        else if (filter === 'sepia') { d[i] = Math.min(255, r * 0.393 + gr * 0.769 + b * 0.189); d[i + 1] = Math.min(255, r * 0.349 + gr * 0.686 + b * 0.168); d[i + 2] = Math.min(255, r * 0.272 + gr * 0.534 + b * 0.131); }
        else if (filter === 'vibrance') { d[i] = Math.min(255, r * 1.2); d[i + 1] = Math.min(255, gr * 1.15); d[i + 2] = Math.min(255, b * 1.1); }
        else if (filter === 'cool') { d[i + 2] = Math.min(255, b * 1.25); }
        else if (filter === 'warm') { d[i] = Math.min(255, r * 1.25); d[i + 1] = Math.min(255, gr * 1.05); }
      }
      ctx.putImageData(imgd, 0, 0);
    };

    const drawFrame = () => {
      if (frame === 'none') return;
      ctx.save();
      if (frame === 'festive') {
        // gold border + emoji corners
        ctx.strokeStyle = '#fbbf24'; ctx.lineWidth = 24; ctx.strokeRect(30, 30, c.width - 60, c.height - 60);
        ctx.font = '80px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText('🪔', 90, 90); ctx.fillText('🪔', c.width - 90, 90); ctx.fillText('🪔', 90, c.height - 90); ctx.fillText('🪔', c.width - 90, c.height - 90);
      } else if (frame === 'confetti') {
        ctx.globalAlpha = 0.35;
        for (let i = 0; i < 60; i++) { ctx.fillStyle = ['#f43f5e','#22d3ee','#fbbf24','#a78bfa'][i%4]; ctx.fillRect(Math.random()*c.width, Math.random()*c.height, 10, 20); }
      } else if (frame === 'polaroid') {
        ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, c.width, 60); ctx.fillRect(0, c.height - 60, c.width, 60);
        ctx.fillRect(0, 0, 60, c.height); ctx.fillRect(c.width - 60, 0, 60, c.height);
      } else if (frame === 'neon') {
        ctx.shadowColor = '#22d3ee'; ctx.shadowBlur = 40; ctx.strokeStyle = '#22d3ee'; ctx.lineWidth = 8;
        ctx.strokeRect(40, 40, c.width - 80, c.height - 80);
      }
      ctx.restore();
    };

    const drawText = () => {
      const bandH = format === 'story' ? 520 : 380;
      ctx.fillStyle = 'rgba(0,0,0,0.72)'; ctx.fillRect(0, c.height - bandH, c.width, bandH);
      ctx.fillStyle = '#facc15'; ctx.fillRect(50, c.height - bandH + 30, 90, 8);
      ctx.fillStyle = '#fff'; ctx.font = 'bold 62px sans-serif'; ctx.textAlign = 'left'; ctx.textBaseline = 'top';
      _wrap(ctx, pick.name, 50, c.height - bandH + 60, c.width - 100, 72);
      if (!whiteLabel) {
        ctx.fillStyle = '#fbbf24'; ctx.font = 'bold 78px sans-serif';
        ctx.fillText(`₹${pick.price}`, 50, c.height - bandH + 220);
      }
      if (pick.discount_badge && !whiteLabel) {
        ctx.save(); ctx.fillStyle = '#dc2626'; ctx.translate(c.width - 220, 180); ctx.rotate(-0.25);
        ctx.beginPath(); ctx.arc(0, 0, 100, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = '#fff'; ctx.font = 'bold 32px sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        _wrap(ctx, pick.discount_badge, 0, -18, 180, 36);
        ctx.restore();
      }
      const shop = cfg.user?.shop || {};
      if (shop.name) {
        ctx.fillStyle = '#fff'; ctx.font = 'bold 30px sans-serif'; ctx.textAlign = 'left';
        ctx.fillText(`🏪 ${shop.name}${shop.whatsapp ? '  ·  📞 ' + shop.whatsapp : ''}`, 50, c.height - 55);
      }
    };

    const drawQR = (onDone) => {
      if (!qrMode) { onDone(); return; }
      const img = new Image(); img.crossOrigin = 'anonymous';
      const qrSize = format === 'story' ? 260 : 200;
      const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=${qrSize}x${qrSize}&data=${encodeURIComponent(productUrl)}&margin=6`;
      img.onload = () => {
        const x = c.width - qrSize - 60, y = c.height - qrSize - (format === 'story' ? 560 : 420);
        ctx.fillStyle = '#fff'; ctx.fillRect(x - 12, y - 12, qrSize + 24, qrSize + 24);
        ctx.drawImage(img, x, y, qrSize, qrSize);
        ctx.fillStyle = '#0f172a'; ctx.font = 'bold 22px sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('📷 Scan to buy', x + qrSize / 2, y + qrSize + 34);
        onDone();
      };
      img.onerror = onDone; img.src = qrUrl;
    };

    const finish = () => { applyFilter(); drawText(); drawFrame(); drawQR(() => {}); };

    if (pick.image_url) {
      const img = new Image(); img.crossOrigin = 'anonymous';
      img.onload = () => {
        const bandH = format === 'story' ? 520 : 380;
        const ratio = Math.max(c.width / img.width, (c.height - bandH) / img.height);
        const w = img.width * ratio, h = img.height * ratio;
        ctx.drawImage(img, (c.width - w) / 2, ((c.height - bandH) - h) / 2, w, h);
        // Soft vignette + shadow for studio look
        const vg = ctx.createRadialGradient(c.width / 2, (c.height - bandH) / 2, c.width * 0.2, c.width / 2, (c.height - bandH) / 2, c.width * 0.7);
        vg.addColorStop(0, 'rgba(0,0,0,0)'); vg.addColorStop(1, 'rgba(0,0,0,0.55)');
        ctx.fillStyle = vg; ctx.fillRect(0, 0, c.width, c.height - bandH);
        finish();
      };
      img.onerror = finish; img.src = pick.image_url;
    } else { finish(); }
  }, [pick, cfg, filter, frame, whiteLabel, format, qrMode, productUrl]);

  if (!active) return <SubscribeGate title="Share Cards locked" body="Product share cards active plan ke saath unlock hote hain." />;

  const _openWith = async (url) => {
    const c = canvasRef.current; if (!c) return;
    const blob = await new Promise((res) => c.toBlob(res, 'image/jpeg', 0.9));
    const imgFile = new File([blob], `${pick.name.replace(/[^a-z0-9]/gi, '_')}.jpg`, { type: 'image/jpeg' });
    const text = buildMessage();

    // Fetch product audio blob if present (3-step opus → download → plain-WA ladder)
    let audioFile = null;
    if (pick.audio_url) {
      try {
        const audUrl = pick.audio_url.startsWith('http') ? pick.audio_url : `${process.env.REACT_APP_BACKEND_URL || ''}${pick.audio_url}`;
        const resp = await fetch(audUrl, { credentials: 'omit' });
        if (resp.ok) {
          const ab = await resp.blob();
          // WhatsApp treats .opus as voice-note; use .opus for native share
          const isOpus = (ab.type || '').includes('opus') || (pick.audio_url || '').match(/\.(opus|ogg)$/i);
          audioFile = new File([ab], isOpus ? 'product-voice.opus' : (pick.audio_name || 'product-audio.mp3'),
                               { type: ab.type || (isOpus ? 'audio/ogg' : 'audio/mpeg') });
        }
      } catch { /* ignore, fall to download */ }
    }

    // Try 1: image + audio together (native share)
    if (audioFile) {
      const combined = { title: pick.name, text, files: [imgFile, audioFile] };
      if (navigator.canShare && navigator.canShare(combined)) {
        try { await navigator.share(combined); toast.success('✅ Shared with voice note'); return; }
        catch (e) { if (e && e.name === 'AbortError') return; }
      }
    }

    // Try 2: image only (native) + auto-download audio
    const imgOnly = { title: pick.name, text, files: [imgFile] };
    if (navigator.canShare && navigator.canShare(imgOnly)) {
      try {
        await navigator.share(imgOnly);
        if (audioFile) {
          const a2 = document.createElement('a'); a2.href = URL.createObjectURL(audioFile);
          a2.download = audioFile.name; document.body.appendChild(a2); a2.click(); document.body.removeChild(a2);
          toast.success('🎙 Voice-note download ho gaya — WA chat me 📎 se attach karein');
        } else {
          toast.success('✅ Shared');
        }
        return;
      } catch (e) { if (e && e.name === 'AbortError') return; }
    }

    // Try 3: hard fallback — download image + audio, open target URL
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = imgFile.name;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    if (audioFile) {
      const a2 = document.createElement('a'); a2.href = URL.createObjectURL(audioFile);
      a2.download = audioFile.name; document.body.appendChild(a2); a2.click(); document.body.removeChild(a2);
    } else if (pick.audio_url) {
      const a2 = document.createElement('a'); a2.href = pick.audio_url; a2.download = 'product-audio'; a2.click();
    }
    window.open(url, '_blank');
    toast.success('⬇️ Card' + (audioFile || pick.audio_url ? ' + voice' : '') + ' downloaded · target khul raha hai — manually attach karein');
  };

  const shareWA  = () => pick && _openWith(`https://wa.me/?text=${encodeURIComponent(buildMessage())}`);
  const shareTG  = () => pick && _openWith(`https://t.me/share/url?url=${encodeURIComponent(productUrl)}&text=${encodeURIComponent(buildMessage())}`);
  const shareFB  = () => pick && _openWith(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(productUrl)}`);
  const shareIG  = () => pick && _openWith(`instagram://library?AssetPath=`); // opens IG on mobile (image downloads first)
  const download = async () => {
    const c = canvasRef.current; if (!c) return;
    const a = document.createElement('a'); a.href = c.toDataURL('image/jpeg', 0.92);
    a.download = `${pick.name.replace(/[^a-z0-9]/gi, '_')}.jpg`; a.click();
  };

  const shopQrUrl = shopSlug ? `https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(`${API_BASE}/api/share/webshop/${shopSlug}`)}&margin=4` : null;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="space-y-2">
          <div className="text-xs opacity-70 font-black">Pick a product</div>
          {rows.length === 0 && <div className="text-xs opacity-60 py-6">Products nahi. Products tab me add karo.</div>}
          {rows.map((p) => (
            <button key={p.id} onClick={() => setPick(p)} data-testid={`webshop-share-pick-${p.id}`}
                    className={`w-full p-2 rounded-xl border-2 text-left flex items-center gap-2 ${pick?.id === p.id ? 'bg-emerald-500/20 border-emerald-400' : 'bg-slate-900 border-slate-700'}`}>
              {p.image_url ? <img src={p.image_url} alt="" className="w-10 h-10 object-cover rounded" /> : <div className="w-10 h-10 rounded bg-slate-800 flex items-center justify-center">📦</div>}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-black truncate">{p.name}</div>
                <div className="text-[10px] opacity-70">₹{p.price}{p.audio_url ? ' · 🎙' : ''}</div>
              </div>
            </button>
          ))}

          {shopQrUrl && (
            <div className="rounded-xl bg-emerald-500/10 border border-emerald-400/40 p-3 text-center space-y-2" data-testid="webshop-shop-qr">
              <div className="text-xs font-black text-emerald-300">🏪 Master Shop QR</div>
              <img src={shopQrUrl} alt="Shop QR" className="mx-auto rounded bg-white p-1 w-40 h-40" />
              <a href={shopQrUrl} download="shop-qr.png" className="inline-block px-3 py-1 rounded bg-emerald-500 text-slate-900 text-[10px] font-black">⬇️ Download Shop QR</a>
              <div className="text-[9px] opacity-70 break-all">{publicShopUrl}</div>
              <PosterMaker shop={cfg.user?.shop} publicShopUrl={publicShopUrl} />
            </div>
          )}
        </div>

        <div className="space-y-2">
          {/* Design studio */}
          <div className="rounded-xl bg-slate-900 border border-slate-700 p-2 space-y-2">
            <div className="text-[10px] font-black text-fuchsia-300 uppercase">🎨 VFX Studio</div>
            <div className="grid grid-cols-3 gap-1">
              {['square', 'story', 'landscape'].map((f) => (
                <button key={f} onClick={() => setFormat(f)} data-testid={`webshop-fmt-${f}`}
                        className={`py-1 rounded text-[10px] font-black ${format === f ? 'bg-fuchsia-500 text-white' : 'bg-slate-800 text-slate-300'}`}>
                  {f === 'square' ? '⬛ 1:1 IG' : f === 'story' ? '📱 9:16 Story' : '🖼 16:9 FB'}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-5 gap-1">
              {['none', 'vibrance', 'warm', 'cool', 'sepia'].map((f) => (
                <button key={f} onClick={() => setFilter(f)} data-testid={`webshop-filter-${f}`}
                        className={`py-1 rounded text-[10px] font-black ${filter === f ? 'bg-cyan-500 text-white' : 'bg-slate-800 text-slate-300'}`}>
                  {f}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-5 gap-1">
              {['none', 'festive', 'confetti', 'polaroid', 'neon'].map((f) => (
                <button key={f} onClick={() => setFrame(f)} data-testid={`webshop-frame-${f}`}
                        className={`py-1 rounded text-[10px] font-black ${frame === f ? 'bg-amber-500 text-slate-900' : 'bg-slate-800 text-slate-300'}`}>
                  {f === 'none' ? 'no frame' : f}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2 text-[10px]">
              <label className="flex items-center gap-1"><input type="checkbox" checked={qrMode} onChange={(e) => setQrMode(e.target.checked)} data-testid="webshop-qr-toggle" /> 📷 Add QR</label>
              <label className="flex items-center gap-1"><input type="checkbox" checked={whiteLabel} onChange={(e) => setWhiteLabel(e.target.checked)} data-testid="webshop-whitelabel" /> 🏷 White-label (hide price)</label>
            </div>
          </div>

          {/* Canvas */}
          <div className="rounded-2xl overflow-hidden border-2 border-emerald-400/40 bg-black">
            <canvas ref={canvasRef} data-testid="webshop-share-canvas" className="w-full h-auto block" />
          </div>

          {/* Message template */}
          <div className="rounded-xl bg-slate-900 border border-slate-700 p-2 space-y-2">
            <div className="text-[10px] font-black text-emerald-300 uppercase">💬 Smart Marketing Message</div>
            <div className="grid grid-cols-5 gap-1">
              {['smart', 'festive', 'sale', 'luxury', 'urgent'].map((k) => (
                <button key={k} onClick={() => setMsgTpl(k)} data-testid={`webshop-msg-${k}`}
                        className={`py-1 rounded text-[10px] font-black ${msgTpl === k ? 'bg-emerald-500 text-slate-900' : 'bg-slate-800 text-slate-300'}`}>
                  {k}
                </button>
              ))}
              <button onClick={() => setMsgTpl('custom')} className={`py-1 rounded text-[10px] font-black ${msgTpl === 'custom' ? 'bg-emerald-500 text-slate-900' : 'bg-slate-800 text-slate-300'}`}>custom</button>
            </div>
            {msgTpl === 'custom' && (
              <textarea value={customMsg} onChange={(e) => setCustomMsg(e.target.value)} rows={3} placeholder="Aapka apna message likhein…" data-testid="webshop-custom-msg"
                        className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            )}
            <textarea readOnly value={buildMessage()} rows={4} data-testid="webshop-msg-preview" className="w-full px-2 py-1.5 rounded bg-slate-800/60 border border-slate-700 text-[10px] opacity-90" />
          </div>

          {/* Multi-platform share */}
          <div className="grid grid-cols-5 gap-1">
            <button onClick={shareWA} disabled={!pick} data-testid="webshop-share-wa" className="py-2 rounded-lg bg-green-600 text-white text-xs font-black disabled:opacity-50">💬 WA</button>
            <button onClick={shareIG} disabled={!pick} data-testid="webshop-share-ig" className="py-2 rounded-lg bg-pink-600 text-white text-xs font-black disabled:opacity-50">📸 IG</button>
            <button onClick={shareTG} disabled={!pick} data-testid="webshop-share-tg" className="py-2 rounded-lg bg-sky-600 text-white text-xs font-black disabled:opacity-50">✈️ TG</button>
            <button onClick={shareFB} disabled={!pick} data-testid="webshop-share-fb" className="py-2 rounded-lg bg-blue-700 text-white text-xs font-black disabled:opacity-50">📘 FB</button>
            <button onClick={download} disabled={!pick} data-testid="webshop-share-dl" className="py-2 rounded-lg bg-emerald-500 text-slate-900 text-xs font-black disabled:opacity-50">⬇️ DL</button>
          </div>

          {/* Per-product QR standalone download */}
          {pick && (
            <a href={`https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=${encodeURIComponent(productUrl)}&margin=4`}
               download={`${pick.name}-qr.png`} target="_blank" rel="noreferrer" data-testid="webshop-product-qr-dl"
               className="block text-center py-1.5 rounded-lg bg-slate-800 text-[10px] font-black text-cyan-300 hover:bg-slate-700">
              🔗 Product-specific QR download
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ ORDERS TAB ============
function OrdersTab({ cfg, active, onReload }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/webshop/orders');
      setRows(r.data.orders || []);
    } catch { toast.error('Load failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { if (active) load(); }, [active, load]);
  if (!active) return <SubscribeGate title="Orders locked" body="Plan tab me jaake trial/subscription activate karo." />;

  const markSeen = async (id) => {
    try { await api.post('/webshop/orders/seen', { order_id: id }); load(); onReload && onReload(); }
    catch { /* ignore */ }
  };
  const markAll = async () => {
    try { await api.post('/webshop/orders/seen', { all: true }); toast.success('✅ All marked as seen'); load(); onReload && onReload(); }
    catch { /* ignore */ }
  };

  const unread = rows.filter((o) => !o.seen).length;
  return (
    <div className="space-y-3" data-testid="webshop-orders-tab">
      <div className="rounded-xl bg-slate-900 border border-slate-700 p-2.5 flex items-center gap-2">
        <Bell size={14} className="text-emerald-300" />
        <div className="flex-1 text-xs">
          <b className="text-emerald-300">{rows.length}</b> orders · {unread > 0 ? <span className="text-rose-300 font-black">{unread} unread</span> : <span className="opacity-60">All seen</span>}
        </div>
        {unread > 0 && (
          <button onClick={markAll} data-testid="webshop-orders-mark-all"
                  className="px-2 py-1 rounded bg-emerald-500 text-slate-900 text-[10px] font-black">
            Mark all seen
          </button>
        )}
        <button onClick={load} data-testid="webshop-orders-refresh" className="px-2 py-1 rounded bg-slate-700 text-white text-[10px] font-black">🔄</button>
      </div>
      {loading && <div className="text-center opacity-60 py-8">⏳ Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="text-center py-10 opacity-60 text-xs">
          Abhi tak koi order nahi mila. Public shop page share karo — jab customer WhatsApp button dabayega, order yahan aayega.
        </div>
      )}
      <div className="space-y-2">
        {rows.map((o) => {
          const shop = cfg.user?.shop || {};
          const shopName = shop.name || 'our shop';
          const productLine = `*${o.product_name}* (₹${o.product_price} × ${o.quantity})`;
          const buyerFirst = (o.buyer_name || '').split(' ')[0] || 'ji';
          const REPLIES = [
            { key: 'confirm', label: '✅ Confirm', text: `Namaste ${buyerFirst}, aapka order confirm ho gaya:\n${productLine}\nHum jaldi hi dispatch/pickup ke liye contact karenge.\n\n— ${shopName}` },
            { key: 'pay',     label: '💳 Payment', text: `Namaste ${buyerFirst}, aapke order ke liye payment link:\n${productLine}\nTotal: ₹${(o.product_price || 0) * (o.quantity || 1)}\n\n[Payment link yahan paste karo]\n\n— ${shopName}` },
            { key: 'delivery',label: '🚚 Delivery', text: `Namaste ${buyerFirst}, aapka order dispatch ho gaya:\n${productLine}\nExpected delivery: 2-3 din me\nAAAA-BBBB-CCCC (tracking)\n\n— ${shopName}` },
            { key: 'pickup',  label: '🏪 Pickup', text: `Namaste ${buyerFirst}, aapka order pickup ke liye ready hai:\n${productLine}\n📍 ${shop.pickup_address || 'Shop address'}\n⏰ Timings: 10 AM - 8 PM\n\n— ${shopName}` },
            { key: 'thanks',  label: '🙏 Thanks', text: `Namaste ${buyerFirst}, order ke liye dhanyawad!\nFuture orders ke liye humein bookmark karein: ${window.location.origin}/webshop/${o.shop_slug}\n\n— ${shopName}` },
          ];
          const sendReply = (t) => {
            if (!o.buyer_phone) { toast.error('Buyer phone missing — cannot send'); return; }
            const p = String(o.buyer_phone).replace(/[^0-9]/g, '');
            window.open(`https://wa.me/${p}?text=${encodeURIComponent(t.text)}`, '_blank');
            if (!o.seen) markSeen(o.id);
          };
          return (
          <div key={o.id} className={`p-3 rounded-xl border-2 ${o.seen ? 'bg-slate-900/60 border-slate-700' : 'bg-emerald-500/10 border-emerald-400/60'}`} data-testid={`webshop-order-${o.id}`}>
            <div className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-black truncate">{o.product_name} <span className="opacity-70 text-[10px]">· ₹{o.product_price} × {o.quantity}</span></div>
                <div className="text-[10px] opacity-80">
                  {o.buyer_name && <>👤 <b>{o.buyer_name}</b> · </>}
                  {o.buyer_phone && <>📞 <b>{o.buyer_phone}</b> · </>}
                  {o.buyer_city && <>📍 {o.buyer_city}</>}
                </div>
                {o.note && <div className="text-[10px] opacity-70 mt-1 italic">📝 {o.note}</div>}
                <div className="text-[9px] opacity-60 mt-0.5">{o.created_at?.replace('T', ' ').slice(0, 16)}</div>
              </div>
              {!o.seen && (
                <button onClick={() => markSeen(o.id)} data-testid={`webshop-order-seen-${o.id}`}
                        className="px-2 py-1 rounded bg-emerald-500 text-slate-900 text-[10px] font-black">✓ Seen</button>
              )}
            </div>
            {o.buyer_phone && (
              <div className="mt-2 flex items-center gap-2">
                <ClientContactActions phone={o.buyer_phone} name={o.buyer_name || 'Buyer'} message={`Namaste ${buyerFirst}, aapke order ke baare me:`} size="xs" testId={`webshop-order-actions-${o.id}`} />
                <div className="flex-1 overflow-x-auto flex gap-1">
                  {REPLIES.map((t) => (
                    <button key={t.key} onClick={() => sendReply(t)}
                            data-testid={`webshop-order-reply-${o.id}-${t.key}`}
                            className="whitespace-nowrap px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-white text-[10px] font-black border border-slate-600">
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          );
        })}
      </div>
    </div>
  );
}

// ============ POSTER MAKER (print-ready shop QR poster) ============
function PosterMaker({ shop, publicShopUrl }) {
  const cvsRef = useRef(null);
  const [themeIdx, setThemeIdx] = useState(0);
  const [cta, setCta] = useState('📷 Scan · Shop · Order via WhatsApp');
  const themes = [
    { name: 'Emerald', bg: ['#059669', '#0f766e', '#facc15'], text: '#fff', accent: '#facc15' },
    { name: 'Royal',   bg: ['#4c1d95', '#7e22ce', '#f472b6'], text: '#fff', accent: '#f9a8d4' },
    { name: 'Sunset',  bg: ['#dc2626', '#ea580c', '#f59e0b'], text: '#fff', accent: '#fef3c7' },
    { name: 'Midnight',bg: ['#0f172a', '#1e293b', '#38bdf8'], text: '#fff', accent: '#38bdf8' },
  ];
  const theme = themes[themeIdx];
  const qrUrl = publicShopUrl ? `https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(publicShopUrl)}&margin=6&ecc=H` : '';

  useEffect(() => {
    const c = cvsRef.current; if (!c || !publicShopUrl) return;
    c.width = 1080; c.height = 1350;
    const ctx = c.getContext('2d');
    // Background gradient
    const g = ctx.createLinearGradient(0, 0, c.width, c.height);
    theme.bg.forEach((col, i) => g.addColorStop(i / (theme.bg.length - 1), col));
    ctx.fillStyle = g; ctx.fillRect(0, 0, c.width, c.height);
    // Diagonal shine
    ctx.save();
    ctx.globalAlpha = 0.15;
    ctx.fillStyle = '#fff';
    ctx.beginPath(); ctx.moveTo(0, c.height * 0.35); ctx.lineTo(c.width, c.height * 0.1);
    ctx.lineTo(c.width, c.height * 0.2); ctx.lineTo(0, c.height * 0.45); ctx.closePath(); ctx.fill();
    ctx.restore();

    // Header band
    ctx.fillStyle = 'rgba(0,0,0,0.35)'; ctx.fillRect(0, 0, c.width, 220);
    ctx.fillStyle = theme.text; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
    ctx.font = 'bold 82px sans-serif';
    ctx.fillText((shop?.name || 'Anant Shop').slice(0, 22), c.width / 2, 100);
    if (shop?.tagline) {
      ctx.font = '38px sans-serif'; ctx.fillStyle = theme.accent;
      ctx.fillText(shop.tagline.slice(0, 40), c.width / 2, 170);
    }

    const drawQR = () => {
      const qrSize = 620;
      const qx = (c.width - qrSize) / 2, qy = 300;
      // white plate
      ctx.fillStyle = '#fff';
      ctx.fillRect(qx - 30, qy - 30, qrSize + 60, qrSize + 60);
      const img = new Image(); img.crossOrigin = 'anonymous';
      img.onload = () => {
        ctx.drawImage(img, qx, qy, qrSize, qrSize);
        // CTA
        ctx.fillStyle = theme.text;
        ctx.font = 'bold 46px sans-serif'; ctx.textAlign = 'center';
        ctx.fillText(cta.slice(0, 40), c.width / 2, qy + qrSize + 100);
        // URL foot
        ctx.font = '28px monospace'; ctx.fillStyle = theme.accent;
        ctx.fillText(publicShopUrl.slice(0, 50), c.width / 2, qy + qrSize + 160);
        // City + WA
        ctx.font = 'bold 34px sans-serif'; ctx.fillStyle = theme.text;
        const contact = [shop?.city && `📍 ${shop.city}`, shop?.whatsapp && `📞 ${shop.whatsapp}`].filter(Boolean).join('    ');
        if (contact) ctx.fillText(contact, c.width / 2, c.height - 80);
        // Watermark
        ctx.font = '22px sans-serif'; ctx.fillStyle = 'rgba(255,255,255,0.5)';
        ctx.fillText('Powered by Anant Smart WebShop', c.width / 2, c.height - 36);
      };
      img.onerror = () => { /* leave QR blank */ };
      img.src = qrUrl;
    };
    drawQR();
  }, [themeIdx, cta, shop, publicShopUrl, qrUrl, theme]);

  const download = () => {
    const c = cvsRef.current; if (!c) return;
    const a = document.createElement('a'); a.href = c.toDataURL('image/jpeg', 0.92);
    a.download = `${(shop?.name || 'shop').replace(/\W+/g, '_')}-poster.jpg`; a.click();
  };
  const print = () => {
    const c = cvsRef.current; if (!c) return;
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.write(`<html><head><title>Shop Poster</title></head><body style="margin:0"><img src="${c.toDataURL('image/jpeg', 0.92)}" style="width:100%"/><script>window.print()</script></body></html>`);
  };
  if (!publicShopUrl) return null;
  return (
    <div className="mt-3 space-y-2" data-testid="webshop-poster-maker">
      <div className="text-[10px] font-black text-emerald-300 uppercase">🖼 Print-Ready Poster (1080×1350)</div>
      <div className="flex gap-1 flex-wrap">
        {themes.map((t, i) => (
          <button key={i} onClick={() => setThemeIdx(i)} data-testid={`webshop-poster-theme-${i}`}
                  className={`px-2 py-0.5 rounded text-[9px] font-black ${themeIdx === i ? 'bg-white text-slate-900' : 'bg-slate-800 text-slate-300'}`}>
            {t.name}
          </button>
        ))}
      </div>
      <input value={cta} onChange={(e) => setCta(e.target.value)} placeholder="Poster CTA" data-testid="webshop-poster-cta"
             className="w-full px-2 py-1 rounded bg-slate-800 border border-slate-700 text-[10px]" />
      <canvas ref={cvsRef} data-testid="webshop-poster-canvas" className="w-full h-auto rounded border-2 border-slate-700 bg-black" />
      <div className="grid grid-cols-2 gap-2">
        <button onClick={download} data-testid="webshop-poster-download" className="py-1.5 rounded bg-emerald-500 text-slate-900 text-[10px] font-black">⬇️ Download JPG</button>
        <button onClick={print} data-testid="webshop-poster-print" className="py-1.5 rounded bg-slate-700 text-white text-[10px] font-black">🖨 Print</button>
      </div>
    </div>
  );
}

// ============ ANALYTICS TAB ============
function AnalyticsTab({ active }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!active) return;
    setLoading(true);
    api.get('/webshop/analytics', { params: { days } }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [active, days]);
  if (!active) return <SubscribeGate title="Analytics locked" body="Plan tab me jaake trial/subscription activate karo." />;

  const maxDaily = Math.max(1, ...(data?.daily || []).map((d) => Math.max(d.shop_views, d.product_views, d.orders)));
  return (
    <div className="space-y-3" data-testid="webshop-analytics-tab">
      <div className="flex items-center gap-2">
        <div className="text-xs opacity-70 font-black">📊 Last</div>
        {[7, 30, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)} data-testid={`webshop-analytics-days-${d}`}
                  className={`px-2 py-0.5 rounded text-[10px] font-black ${days === d ? 'bg-emerald-500 text-slate-900' : 'bg-slate-800 text-slate-300'}`}>
            {d}d
          </button>
        ))}
      </div>
      {loading && <div className="text-center opacity-60 py-6">⏳ Loading…</div>}
      {data && (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="webshop-analytics-kpi">
            <KPI label="Shop views"    value={data.shop_views}     color="emerald" />
            <KPI label="Product views" value={data.product_views}  color="cyan" />
            <KPI label="Orders"        value={data.orders}         color="fuchsia" />
            <KPI label="Conversion"    value={`${data.conversion_pct}%`} color="amber" />
          </div>

          {/* Daily bar chart (SVG, dependency-free) */}
          <div className="rounded-2xl bg-slate-900 border border-slate-700 p-3">
            <div className="text-[10px] font-black text-emerald-300 uppercase mb-2">📈 Daily activity</div>
            {data.daily?.length === 0 && <div className="text-xs opacity-60 py-6 text-center">Abhi tak koi views nahi.</div>}
            {data.daily?.length > 0 && (
              <div className="overflow-x-auto">
                <div className="flex items-end gap-1 h-40 min-w-full" style={{ minWidth: `${Math.max(320, data.daily.length * 30)}px` }} data-testid="webshop-analytics-chart">
                  {data.daily.map((d) => (
                    <div key={d.day} className="flex-1 flex flex-col items-center gap-0.5" title={`${d.day}\n👁 shop: ${d.shop_views}\n📦 product: ${d.product_views}\n🛒 orders: ${d.orders}`}>
                      <div className="flex items-end gap-0.5 h-32 w-full">
                        <div className="flex-1 bg-emerald-500 rounded-t" style={{ height: `${(d.shop_views / maxDaily) * 100}%` }} />
                        <div className="flex-1 bg-cyan-500 rounded-t"    style={{ height: `${(d.product_views / maxDaily) * 100}%` }} />
                        <div className="flex-1 bg-fuchsia-500 rounded-t" style={{ height: `${(d.orders / maxDaily) * 100}%` }} />
                      </div>
                      <div className="text-[8px] opacity-60 whitespace-nowrap">{d.day.slice(5)}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-3 text-[10px] mt-2 opacity-80">
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 inline-block" /> Shop views</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-cyan-500 inline-block" /> Product views</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 bg-fuchsia-500 inline-block" /> Orders</span>
            </div>
          </div>

          {/* Top products */}
          <div className="rounded-2xl bg-slate-900 border border-slate-700 p-3">
            <div className="text-[10px] font-black text-emerald-300 uppercase mb-2">🏆 Top products</div>
            {data.top_products?.length === 0 && <div className="text-xs opacity-60 py-4 text-center">Koi product view nahi mila.</div>}
            <div className="space-y-1">
              {data.top_products?.map((p, i) => (
                <div key={p.product_id} className="flex items-center gap-2 text-xs" data-testid={`webshop-analytics-top-${p.product_id}`}>
                  <div className="w-6 text-center font-black opacity-70">#{i + 1}</div>
                  <div className="flex-1 truncate">{p.name} <span className="opacity-60 text-[10px]">₹{p.price || '-'}</span></div>
                  <div className="text-cyan-300 text-[10px]">👁 {p.views}</div>
                  <div className="text-fuchsia-300 text-[10px]">🛒 {p.orders}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function KPI({ label, value, color }) {
  const map = {
    emerald: 'bg-emerald-500/15 border-emerald-400/40 text-emerald-300',
    cyan:    'bg-cyan-500/15 border-cyan-400/40 text-cyan-300',
    fuchsia: 'bg-fuchsia-500/15 border-fuchsia-400/40 text-fuchsia-300',
    amber:   'bg-amber-500/15 border-amber-400/40 text-amber-300',
  };
  return (
    <div className={`rounded-xl border ${map[color] || map.emerald} p-3 text-center`}>
      <div className="text-2xl font-black">{value}</div>
      <div className="text-[10px] opacity-70 uppercase">{label}</div>
    </div>
  );
}

// ============ PLAN TAB ============
function PlanTab({ cfg, onReload }) {
  const [busy, setBusy] = useState(null);
  const s = cfg.settings || {};
  const active = !!cfg.active;
  const trial = cfg.user?.plan === 'trial';
  const paid = cfg.user?.plan === 'monthly' || cfg.user?.plan === 'yearly';
  const expiry = cfg.user?.plan_expiry;

  const subscribe = async (plan, renew = false) => {
    const price = plan === 'monthly' ? s.monthly_price_at : s.yearly_price_at;
    const msg = renew
      ? `Plan already active. Early renew karke aur ${plan === 'monthly' ? s.monthly_days : s.yearly_days} days add karna hai? ${price} AT deduct hoga.`
      : `${price} AT deduct hoga. Continue?`;
    if (!window.confirm(msg)) return;
    setBusy(plan);
    try {
      await api.post('/webshop/subscribe', { plan, renew });
      toast.success(renew ? `✅ Renewed · +${price} AT` : `✅ ${plan} plan activated`);
      onReload && onReload();
    } catch (e) {
      if (e?.response?.status === 409) {
        if (window.confirm(`${e.response.data.detail}\n\nEarly renew karna hai? (${price} AT + days add ho jayenge)`)) return subscribe(plan, true);
        return;
      }
      toast.error(e?.response?.data?.detail || 'Failed');
    } finally { setBusy(null); }
  };
  return (
    <div className="space-y-3">
      {active && (
        <div className="rounded-2xl bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border-2 border-emerald-400/50 p-4 text-center" data-testid="webshop-plan-active">
          <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-black">🎉 {trial ? 'Trial' : 'Plan'} Active · <span className="text-white">{cfg.user.plan}</span></div>
          <CountdownTimer expiry={expiry} className="text-4xl sm:text-5xl font-black text-emerald-200 tabular-nums mt-1" onExpire={onReload} />
          <div className="text-[10px] opacity-70 mt-1">Expires · {expiry ? new Date(expiry).toLocaleString() : '—'}</div>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className={`p-4 rounded-2xl border-2 ${paid && active ? 'bg-slate-800/40 border-slate-600' : 'bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border-emerald-400/40'}`} data-testid="webshop-plan-monthly">
          <div className="text-lg font-black">Monthly</div>
          <div className={`text-3xl font-black ${paid && active ? 'text-slate-400' : 'text-emerald-300'}`}>{s.monthly_price_at} AT</div>
          <div className="text-xs opacity-70">{s.monthly_days} days · Unlimited products · AI 20 AT/each</div>
          <button onClick={() => subscribe('monthly')} disabled={busy === 'monthly'} data-testid="webshop-plan-monthly-btn"
                  className={`w-full mt-3 py-2 rounded-lg text-sm font-black disabled:opacity-50 ${paid && active ? 'bg-slate-700 text-slate-300' : 'bg-emerald-500 text-slate-900'}`}>
            {busy === 'monthly' ? '⏳' : (paid && active ? '🔒 Active · Early Renew' : `Buy Monthly · ${s.monthly_price_at} AT`)}
          </button>
        </div>
        <div className={`p-4 rounded-2xl border-2 ${paid && active ? 'bg-slate-800/40 border-slate-600' : 'bg-gradient-to-br from-amber-500/20 to-orange-500/20 border-amber-400/40'}`} data-testid="webshop-plan-yearly">
          <div className="text-lg font-black">Yearly · <span className="text-amber-300">2 mo free</span></div>
          <div className={`text-3xl font-black ${paid && active ? 'text-slate-400' : 'text-amber-300'}`}>{s.yearly_price_at} AT</div>
          <div className="text-xs opacity-70">{s.yearly_days} days · Best value</div>
          <button onClick={() => subscribe('yearly')} disabled={busy === 'yearly'} data-testid="webshop-plan-yearly-btn"
                  className={`w-full mt-3 py-2 rounded-lg text-sm font-black disabled:opacity-50 ${paid && active ? 'bg-slate-700 text-slate-300' : 'bg-amber-500 text-slate-900'}`}>
            {busy === 'yearly' ? '⏳' : (paid && active ? '🔒 Active · Early Renew' : `Buy Yearly · ${s.yearly_price_at} AT`)}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============ SHARED HELPERS ============
function PlanStatusBadge({ cfg }) {
  const active = !!cfg?.active;
  const expiry = cfg?.user?.plan_expiry;
  if (!active) return <div className="text-[10px] px-3 py-1 rounded-full border-2 border-amber-400/60 bg-amber-500/20 text-amber-200 font-black">🔒 No Plan</div>;
  return (
    <div className="text-[10px] px-3 py-1 rounded-full border-2 border-emerald-400/60 bg-emerald-500/20 text-emerald-200 font-black inline-flex items-center gap-1">
      <span>{cfg.user.plan === 'trial' ? '🎁 Trial' : '✅ Active'}</span>
      <CountdownTimer expiry={expiry} compact />
    </div>
  );
}

function CountdownTimer({ expiry, className = '', compact = false, onExpire }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  let diffSec = 0;
  if (expiry) { try { diffSec = Math.max(0, Math.floor((new Date(expiry).getTime() - now) / 1000)); } catch { diffSec = 0; } }
  const expired = diffSec === 0;
  useEffect(() => { if (expired && onExpire) onExpire(); }, [expired, onExpire]);
  if (!expiry) return null;
  const d = Math.floor(diffSec / 86400);
  const h = Math.floor((diffSec % 86400) / 3600);
  const m = Math.floor((diffSec % 3600) / 60);
  const sec = diffSec % 60;
  const pad = (n) => String(n).padStart(2, '0');
  if (compact) return <span className={className}>· {pad(d)}d:{pad(h)}h:{pad(m)}m:{pad(sec)}s</span>;
  return (
    <div className={className} data-testid="webshop-countdown">
      {pad(d)}<span className="opacity-60 text-2xl">d</span>:{pad(h)}<span className="opacity-60 text-2xl">h</span>:{pad(m)}<span className="opacity-60 text-2xl">m</span>:{pad(sec)}<span className="opacity-60 text-2xl">s</span>
    </div>
  );
}

function SubscribeGate({ title, body }) {
  const goPlan = () => { const el = document.querySelector('[data-testid="webshop-tab-plan"]'); el && el.click(); };
  return (
    <div className="rounded-2xl border-2 border-amber-400/50 bg-gradient-to-br from-amber-500/15 to-emerald-500/15 p-6 sm:p-10 text-center space-y-3" data-testid="webshop-tab-locked">
      <div className="text-5xl">🔒</div>
      <div className="text-lg sm:text-xl font-black text-amber-200">{title}</div>
      {body && <div className="text-xs sm:text-sm opacity-80 max-w-md mx-auto">{body}</div>}
      <button onClick={goPlan} className="inline-block px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-amber-500 text-slate-900 text-sm font-black">🚀 Plan tab me jaao</button>
    </div>
  );
}

function _wrap(ctx, text, x, y, maxWidth, lineHeight) {
  const words = String(text || '').split(/\s+/);
  let line = ''; let cy = y;
  for (const w of words) {
    const test = line ? line + ' ' + w : w;
    if (ctx.measureText(test).width > maxWidth && line) { ctx.fillText(line, x, cy); line = w; cy += lineHeight; }
    else { line = test; }
    if (cy > y + lineHeight * 3.5) break;
  }
  if (line) ctx.fillText(line, x, cy);
}
